/**
 * Player Level Definitions for Padel Brothers
 * Uses Glicko-2 rating ranges
 */

export interface LevelDefinition {
    title: string;
    ratingRange: string;
    eloRange: string; // Alias for backward compatibility
    minRating: number;
    maxRating: number;
    desc: string;
    details: string[];
}

// Default Glicko-2 rating for new players
export const DEFAULT_RATING = 1500;
// Alias for backward compatibility during ELO->Rating transition
export const DEFAULT_ELO = DEFAULT_RATING;

export const LEVEL_DEFINITIONS: LevelDefinition[] = [
    { title: "common.levels.1", ratingRange: "< 1350", eloRange: "< 1350", minRating: 0, maxRating: 1349, desc: "common.levels.1_desc", details: [] },
    { title: "common.levels.2", ratingRange: "1350-1450", eloRange: "1350-1450", minRating: 1350, maxRating: 1449, desc: "common.levels.2_desc", details: [] },
    { title: "common.levels.3", ratingRange: "1450-1550", eloRange: "1450-1550", minRating: 1450, maxRating: 1549, desc: "common.levels.3_desc", details: [] },
    { title: "common.levels.4", ratingRange: "1550-1650", eloRange: "1550-1650", minRating: 1550, maxRating: 1649, desc: "common.levels.4_desc", details: [] },
    { title: "common.levels.5", ratingRange: "1650-1750", eloRange: "1650-1750", minRating: 1650, maxRating: 1749, desc: "common.levels.5_desc", details: [] },
    { title: "common.levels.6", ratingRange: "1750-1850", eloRange: "1750-1850", minRating: 1750, maxRating: 1849, desc: "common.levels.6_desc", details: [] },
    { title: "common.levels.7", ratingRange: "1850-2000", eloRange: "1850-2000", minRating: 1850, maxRating: 1999, desc: "common.levels.7_desc", details: [] },
    { title: "common.levels.8", ratingRange: "> 2000", eloRange: "> 2000", minRating: 2000, maxRating: Infinity, desc: "common.levels.8_desc", details: [] }
];


/**
 * Get player level number (1-8) from rating
 */
export const getLevelFromRating = (rating: number): number => {
    for (let i = LEVEL_DEFINITIONS.length - 1; i >= 0; i--) {
        if (rating >= LEVEL_DEFINITIONS[i].minRating) {
            return i + 1;
        }
    }
    return 1;
};

/**
 * Get level title from rating
 */
export const getLevelTitleFromRating = (rating: number): string => {
    const level = getLevelFromRating(rating);
    return LEVEL_DEFINITIONS[level - 1]?.title || "common.levels.1";
};

// Alias for compatibility
export const getLevelName = getLevelTitleFromRating;


/**
 * Fixed initial ratings for each level.
 * These values ensure every player at the same level gets the exact same starting rating.
 */
const FIXED_RATINGS_PER_LEVEL: Record<number, number> = {
    1: 1300,
    2: 1400,
    3: 1500,
    4: 1600,
    5: 1700,
    6: 1800,
    7: 1900,
    8: 2050
};

/**
 * Get initial rating for a given level (fixed value)
 */
export const getInitialRatingFromLevel = (level: string | number): number => {
    const levelNum = parseInt(String(level).charAt(0)) || 1;
    return FIXED_RATINGS_PER_LEVEL[levelNum] || 1500;
};

// Alias for backward compatibility
export const getInitialEloFromLevel = getInitialRatingFromLevel;

/**
 * Get initial RD (Rating Deviation) for a given level
 * Lower RD for higher levels (more certain of their skill)
 */
export const getInitialRdFromLevel = (level: string | number): number => {
    const levelNum = parseInt(String(level).charAt(0)) || 1;
    // New players start with high RD (350), experienced players with lower RD
    const baseRd = 350;
    const rdReduction = (levelNum - 1) * 30; // Reduce RD by 30 per level above 1
    return Math.max(150, baseRd - rdReduction);
};

/**
 * Translates a level string (e.g., "7. Niveau expert") using translation keys
 * Extracts the level number and uses common.levels.N
 */
export const translateLevel = (levelStr: string | number | undefined, t: any): string => {
    if (levelStr === undefined || levelStr === null || levelStr === '') return t('playerProfile.noLevel', 'Niveau non défini');

    const str = String(levelStr);
    // Extract level number from string like "7. Niveau expert" or just "7"
    const levelNum = parseInt(str.charAt(0));
    if (isNaN(levelNum) || levelNum < 1 || levelNum > 8) return str;

    return t(`common.levels.${levelNum}`);
};
